import { useState } from 'react'
import { MapPin, Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import AddressSearchModal from '@/components/facility/AddressSearchModal'
import { toast } from '@/components/ui/Toast'
import type { LocationSearchResult } from '@/features/location/api'

interface LocationSelectorProps {
  /** 위치 유형 */
  locationType: 'gps' | 'search' | null
  /** 주소 텍스트 */
  addressText: string | null
  /** 위치 설정 핸들러 */
  onSetLocation: (data: {
    type: 'gps' | 'search'
    latitude: number
    longitude: number
    addressText: string
  }) => void
  /** 위치 초기화 핸들러 */
  onClearLocation: () => void
}

/**
 * 위치 선택 컴포넌트.
 *
 * 2가지 방법으로 위치를 설정할 수 있습니다:
 * 1. 현재 위치 (GPS)
 * 2. 주소 검색 (TODO: 카카오 주소 API 연동)
 */
export default function LocationSelector({
  locationType,
  addressText,
  onSetLocation,
  onClearLocation,
}: LocationSelectorProps) {
  const [isLoadingGps, setIsLoadingGps] = useState(false)
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false)

  /** 역지오코딩: 좌표 → 주소명 */
  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(`/api/v1/location/reverse-geocode?latitude=${lat}&longitude=${lng}`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data?.addressName) {
          return data.data.addressName
        }
      }
    } catch { /* fallback */ }
    return '내 위치 (주소 변환 실패)'
  }

  /** GPS 위치 가져오기 */
  const handleGpsClick = async () => {
    if (!navigator.geolocation) {
      toast.error('위치 서비스를 지원하지 않는 기기입니다.')
      return
    }

    setIsLoadingGps(true)

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        const address = await reverseGeocode(latitude, longitude)

        onSetLocation({
          type: 'gps',
          latitude,
          longitude,
          addressText: address,
        })
        setIsLoadingGps(false)
      },
      (error) => {
        setIsLoadingGps(false)
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('위치 권한을 허용해 주세요.')
            break
          case error.POSITION_UNAVAILABLE:
            toast.error('위치 정보를 가져올 수 없습니다.')
            break
          case error.TIMEOUT:
            toast.error('위치 요청 시간이 초과되었습니다.')
            break
          default:
            toast.error('위치를 가져오는 중 문제가 발생했습니다.')
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    )
  }

  /** 주소 검색 결과 선택 */
  const handleAddressSelect = (result: LocationSearchResult) => {
    onSetLocation({
      type: 'search',
      latitude: result.latitude,
      longitude: result.longitude,
      addressText: result.addressName,
    })
  }

  /** 위치가 설정된 상태 */
  if (locationType && addressText) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 rounded-[var(--radius-card)] border-2 border-cb-primary bg-cb-primary-light p-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cb-primary">
            <MapPin size={20} className="text-white" />
          </div>
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-caption font-medium text-cb-primary">
              {locationType === 'gps' ? '현재 위치' : '검색된 주소'}
            </span>
            <span className="text-body-sm font-medium text-cb-gray-900">
              {addressText}
            </span>
          </div>
          <button
            type="button"
            onClick={onClearLocation}
            className="flex-shrink-0 rounded-full bg-white px-3 py-1 text-caption font-medium text-cb-primary hover:bg-cb-primary-muted transition-colors"
            aria-label="위치 변경"
          >
            변경
          </button>
        </div>
      </div>
    )
  }

  /** 위치 미설정 상태 */
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          loading={isLoadingGps}
          onClick={handleGpsClick}
          aria-label="현재 위치로 설정"
        >
          <MapPin size={18} />
          <span>현재 위치</span>
        </Button>

        <Button
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          onClick={() => setIsAddressSearchOpen(true)}
          aria-label="주소 검색"
        >
          <Search size={18} />
          <span>주소 검색</span>
        </Button>
      </div>

      <p className="text-caption text-cb-gray-400 text-center">
        위치를 설정하면 가까운 시설을 추천해 드려요
      </p>

      <AddressSearchModal
        isOpen={isAddressSearchOpen}
        onClose={() => setIsAddressSearchOpen(false)}
        onSelect={handleAddressSelect}
      />
    </div>
  )
}
